import React from 'react';

const Features = () => {
  const securityFeatures = [
    {
      icon: "prompt",
      title: "Prompt Injection Attacks",
      description: "Protect against malicious inputs that can compromise your AI systems"
    },
    {
      icon: "data",
      title: "Data Leakage",
      description: "Prevent unauthorized access to sensitive training data and IP"
    },
    {
      icon: "compliance",
      title: "Compliance Violations",
      description: "Stay compliant with AI regulations and frameworks"
    },
    {
      icon: "brand",
      title: "Brand Reputation",
      description: "Maintain trust by preventing AI security incidents"
    }
  ];

  const redTeamingFeatures = [
    {
      icon: "vulnerability",
      title: "Automatic Vulnerability Detection",
      description: "Find critical AI vulnerabilities in minutes, not months"
    },
    {
      icon: "threat",
      title: "Advanced Threat Intelligence",
      description: "Powered by the latest research in AI security"
    },
    {
      icon: "remediation",
      title: "Actionable Remediation",
      description: "Get specific recommendations to fix vulnerabilities, not just alerts"
    }
  ];

  const renderIcon = (iconName) => {
    switch(iconName) {
      case 'prompt':
        return (
          <svg className="w-10 h-10 text-red-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon>
          </svg>
        );
      case 'data':
        return (
          <svg className="w-10 h-10 text-yellow-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
          </svg>
        );
      case 'compliance':
        return (
          <svg className="w-10 h-10 text-blue-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
            <polyline points="14 2 14 8 20 8"></polyline>
            <path d="M9 15l3 3 5-5"></path>
          </svg>
        );
      case 'brand':
        return (
          <svg className="w-10 h-10 text-purple-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
          </svg>
        );
      case 'vulnerability':
        return (
          <svg className="w-10 h-10 text-yellow-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
        );
      case 'threat':
        return (
          <svg className="w-10 h-10 text-blue-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 12h-4l-3 9L9 3l-3 9H2"></path>
          </svg>
        );
      case 'remediation':
        return (
          <svg className="w-10 h-10 text-green-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
            <polyline points="22 4 12 14.01 9 11.01"></polyline>
          </svg>
        );
      default:
        return null;
    }
  };

  return (
    <div>
      <section className="py-16 bg-black text-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Untested AI Applications Leave Organizations Vulnerable</h2>
            <p className="text-gray-400 max-w-3xl mx-auto">
              New AI-specific attacks emerge daily, requiring continuous red teaming to stay ahead of evolving threats
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12">
            {securityFeatures.map((feature, index) => (
              <div key={index} className="rounded-lg p-6 bg-gray-900 shadow-lg border border-gray-800">
                <div className="mb-4 inline-block">
                  {renderIcon(feature.icon)}
                </div>
                <h3 className="text-lg font-semibold mb-2 text-white">{feature.title}</h3>
                <p className="text-gray-400">{feature.description}</p>
              </div>
            ))}
          </div>

        {/* Stats Section with 42% statistic and hero image background color */}
        <section className="rounded-lg p-6 bg-gray-900 shadow-lg border border-gray-800" style={{ backgroundColor: '#111827' }}> {/* This is the background color from hero-image.svg */}
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold mb-4">
            According to Gartner AI TRiSM 2025 Report, continuous automated testing and code scans are essential parts of a red teaming program for a trustworthy and secure AI implementation.            </h2>
          </div>
        </section>
        </div>
      </section>
      
      <section className="py-16 bg-gray-900 text-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Comprehensive AI Red Teaming</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {redTeamingFeatures.map((feature, index) => (
              <div key={index} className="flex flex-col items-center text-center">
                <div className="mb-4 inline-block">
                  {renderIcon(feature.icon)}
                </div>
                <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                <p className="text-gray-400">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Features;